import React, { useCallback, useMemo, useState } from 'react';
import { Dropdown } from 'antd';
import { UserOutlined, DownOutlined, LogoutOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@context/ThemeContext';
import { useAppDispatch } from '@store/helpers';
import { adminApi } from '@store/api/adminApi';
import { logout } from '@store/slices/adminAuthSlice';
import { persistor } from '@store/store';
import styles from './AppUserMenuDropdown.module.css';

type AppUserMenuDropdownProps = {
  email?: string | null;
  role?: string | null;
  isMobile?: boolean;
};

const formatRoleLabel = (role?: string | null) => {
  if (!role) {
    return 'Admin';
  }

  return role
    .split(/[_-\s]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const AppUserMenuDropdown: React.FC<AppUserMenuDropdownProps> = ({
  email,
  role,
  isMobile = false,
}) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { colors, theme, setTheme } = useTheme();

  const roleLabel = useMemo(() => formatRoleLabel(role), [role]);

  const handleLogout = useCallback(async () => {
    dispatch(logout());
    dispatch(adminApi.util.resetApiState());
    await persistor.purge();
    setOpen(false);
    navigate('/login', { replace: true });
  }, [dispatch, navigate]);

  const dropdownContent = (
    <div className={styles.dropdown}>
      <div className={styles.dropdownUser}>
        <div className={styles.dropdownAvatar}>
          <UserOutlined />
        </div>
        <div className={styles.dropdownMeta}>
          <span className={styles.dropdownEmail}>{email || 'Admin'}</span>
          <span className={styles.dropdownRole}>{roleLabel}</span>
        </div>
      </div>
      <div className={styles.dropdownDivider} />
      <div className={styles.themeSection}>
        <span className={styles.themeLabel}>Theme</span>
        <div className={styles.themeSwitcher}>
          <button
            type="button"
            className={`${styles.themeButton} ${theme === 'dark' ? styles.themeButtonActive : ''}`}
            onClick={() => setTheme('dark')}
          >
            Dark
          </button>
          <button
            type="button"
            className={`${styles.themeButton} ${theme === 'darker' ? styles.themeButtonActive : ''}`}
            onClick={() => setTheme('darker')}
          >
            Darker
          </button>
        </div>
      </div>
      <div className={styles.dropdownDivider} />
      <button type="button" className={styles.optionLogout} onClick={() => void handleLogout()}>
        <LogoutOutlined />
        <span>Log out</span>
      </button>
    </div>
  );

  return (
    <Dropdown
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      menu={{ items: [] }}
      popupRender={() => dropdownContent}
      overlayClassName={styles.dropdownOverlay}
      placement="bottomRight"
    >
      <button
        type="button"
        className={styles.button}
        aria-label={email || 'Admin menu'}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className={styles.trigger}>
          {isMobile ? (
            <span className={styles.triggerIconWrap}>
              <UserOutlined className={styles.triggerIcon} style={{ color: colors.text_500 }} />
            </span>
          ) : (
            <>
              <span className={styles.triggerUserName}>{email || 'Admin'}</span>
              <DownOutlined className={styles.triggerArrow} style={{ color: colors.text_500 }} />
            </>
          )}
        </span>
      </button>
    </Dropdown>
  );
};
